<ph-drawer *ngIf="!isExpanded"
           [open]="isOpen"
           modeless
           resizable
           bleed
           no-close
           [width]="widthPx"
           [minWidth]="320"
           [maxWidth]="720"
           aria-label="Gable AI Assistant"
           (ph-resize)="onDrawerResize($event.detail)"
           (ph-close)="onClose()">
    <ng-container *ngTemplateOutlet="gableShell"></ng-container>
</ph-drawer>

<ph-dialog *ngIf="isExpanded"
           [open]="isOpen"
           size="full"
           width="1140"
           fill
           flush
           no-close>
    <ng-container *ngTemplateOutlet="gableShell"></ng-container>
</ph-dialog>

<app-gable-approval-dialog *ngIf="reviewProposal"
                           [open]="true"
                           [proposal]="reviewProposal"
                           (closed)="onApprovalClosed($event)"></app-gable-approval-dialog>

<ng-template #gableShell>
<div class="gable-shell" [class.is-expanded]="isExpanded">
    <!-- ============================================================ -->
    <!-- View tabs — Chat + Activity (both live, SD-7969). Kill        -->
    <!-- switch: localStorage 'gable-autopilot-ui' = 'off'.            -->
    <!-- ============================================================ -->


    <!-- ============================================================ -->
    <!-- Drawer-mode navigation menu (off the title caret)            -->
    <!-- ============================================================ -->
    <!-- Mirrors the expanded rail: same nav rows, same recents, same archived
         entry. The drawer has no room for a permanent rail, so the title names
         where you are and the caret is how you leave. This replaced the tab
         strip that used to sit under the header — the two were redundant, and
         the tabs couldn't reach recents or archived at all.
         Gates are copied from the rail verbatim, NOT from the old tab strip:
         Activity on `autopilotUi`, Preferences ungated (SD-9302), Skills
         super-only (SD-9197). -->


    <!-- ============================================================ -->
    <!-- CONTENT ROW — expanded mode puts a history sidebar to the     -->
    <!-- left of the chat column; drawer mode collapses the sidebar.   -->
    <!-- ============================================================ -->
    <div class="gable-content-row">

    <!-- ============================================================ -->
    <!-- SD-6742 — expanded-mode history sidebar (per Figma)          -->
    <!-- ============================================================ -->
    <ph-chat-rail *ngIf="isExpanded"
                  [sections]="railSections"
                  [recents]="railRecents"
                  [footer]="railFooter"
                  [value]="railValue"
                  [highlight]="historySearch"
                  [editing]="editingConversationId !== null ? '' + editingConversationId : ''"
                  row-actions
                  (ph-change)="onRailChange($event.detail)"
                  (ph-end-reached)="onRailEndReached()">
        <ph-input slot="search"
                  icon="magnifying-glass"
                  placeholder="Search chats"
                  aria-label="Search chats"
                  aria-describedby="gable-find-hint"
                  clearable
                  [value]="historySearch"
                  (input)="onRailSearchInput($event)"
                  (keydown)="onHistorySearchKeydown($event)">
            <ng-container *ngIf="showFindControls">
                <span slot="trailing" aria-hidden="true">{{ matchCount > 0 ? matchActiveIndex + 1 : 0 }} / {{ matchCount }}</span>
                <ph-compact-button slot="trailing" variant="ghost" size="medium" icon="caret-up"
                                   aria-label="Previous match" [disabled]="matchCount === 0"
                                   (click)="onFindPrev()"></ph-compact-button>
                <ph-compact-button slot="trailing" variant="ghost" size="medium" icon="caret-down"
                                   aria-label="Next match" [disabled]="matchCount === 0"
                                   (click)="onFindNext()"></ph-compact-button>
            </ng-container>
        </ph-input>
        <span class="sr-only" id="gable-find-hint">Press Enter for the next match, Shift plus Enter for the previous, Escape to clear.</span> <!-- kitlint-allow-sr-only: the find hint has no visible home -->
        <span class="sr-only" role="status" aria-live="polite" *ngIf="showFindControls">{{ matchCount > 0 ? 'Match ' + (matchActiveIndex + 1) + ' of ' + matchCount : 'No matches' }}</span> <!-- kitlint-allow-sr-only: announces the match counter -->

        <ph-dropdown *ngFor="let c of activeConversations; trackBy: trackByConversationId"
                     [attr.slot]="'recent-actions-' + c.id"
                     label="Chat options"
                     align="end"
                     trigger-variant="ghost"
                     trigger-size="medium"
                     [items]="railChatMenu"
                     (ph-select)="onRailChatMenu($event.detail, c)"></ph-dropdown>

        <ph-input *ngIf="editingConversation as c"
                  [attr.slot]="'recent-edit-' + c.id"
                  autofocus
                  maxlength="255"
                  aria-label="Rename chat"
                  [value]="editingTitle"
                  (input)="onRailRenameInput($event)"
                  (keydown.enter)="$event.preventDefault(); commitInlineRename(c)"
                  (keydown.escape)="$event.preventDefault(); cancelInlineRename()"
                  (focusout)="commitInlineRename(c)"></ph-input>

        <ph-skeleton slot="list-end" *ngIf="conversationsLoading" shape="list" rows="3" label="Loading chats"></ph-skeleton>
        <ph-hint slot="list-end" *ngIf="!conversationsLoading && activeConversations.length === 0 && isSearchingHistory">No chats match your search.</ph-hint>
    </ph-chat-rail>

    <div class="gable-main">

        <!-- ============================================================ -->
        <!-- HEADER                                                       -->
        <!-- ============================================================ -->
        <header class="gable-header">
            <div class="gable-title-col">
                <!-- Named explicitly: the info button below is a descendant, and
                     accessible-name computation concatenates descendant names, so
                     without this the heading announces "Instructions About
                     Instructions". The button keeps its own name and stays
                     operable; only the h2's name is pinned to the title. -->
                <h2 class="gable-title" [attr.aria-label]="chatHeaderTitle">
                        <!-- SD-6742: in drawer mode the title names the active view
                             (the open chat, or Activity / Preferences / Skills) and
                             its caret opens the nav menu — the drawer's stand-in for
                             the expanded rail. Expanded mode has the real rail, so
                             the title stays a plain label there. -->
                        <!-- Rename happens where the title already is, in either
                             mode — no dialog, and no hunting for the chat's row. -->
                        <ph-input
                            *ngIf="editingHeaderTitle; else headerTitleDisplay"
                            autofocus
                            maxlength="255"
                            aria-label="Rename chat"
                            ngDefaultControl
                            [(ngModel)]="headerTitleDraft"
                            (keydown.enter)="$event.preventDefault(); commitHeaderRename()"
                            (keydown.escape)="$event.preventDefault(); cancelHeaderRename()"
                            (focusout)="commitHeaderRename()"></ph-input>
                        <ng-template #headerTitleDisplay>
                        <ng-container *ngIf="!isExpanded; else plainTitle">
                            <ph-chat-menu [title]="chatHeaderTitle"
                                          [sections]="railSections"
                                          [recents]="menuRecents"
                                          [footer]="railFooter"
                                          [value]="railValue"
                                          (ph-open)="onHistoryMenuOpened()"
                                          (ph-change)="onRailChange($event.detail)"></ph-chat-menu>
                        </ng-container>
                        <ng-template #plainTitle>
                            <span class="gable-title-text">{{ chatHeaderTitle }}</span>
                        </ng-template>
                        </ng-template>
                    <!-- The panel's description hangs off the title the whole
                         drawer already shows, so no panel has to spend a header
                         block on it. -->
                    <ng-container *ngIf="activeTabInfo as info">
                        <ph-tooltip [text]="info">
                            <ph-compact-button variant="ghost" size="large" icon="info" [attr.aria-label]="'About ' + chatHeaderTitle"></ph-compact-button>
                        </ph-tooltip>
                    </ng-container>
                </h2>
                <!-- Tenant of the open chat, moved off the sidebar rows. Empty
                     (and unrendered) whenever the chat belongs to the scope the
                     user is already in, so it only speaks up when it matters. -->
                <span *ngIf="headerScopeLabel" class="gable-title-scope">{{ headerScopeLabel }}</span>
                <!-- The "Up to date" / "N suggestions need review" status line used
                     to render here (SD-7969). Parked: it read as chat state rather
                     than as the background review queue it actually counts. The data
                     still flows (`pendingProposals`), the Activity deep-link now
                     lives in the title menu, and the `.gable-status-line` styles are
                     still in the SCSS for whenever it comes back. -->
            </div>
            <!-- Manage the OPEN chat. Present in every mode, which is the point:
                 rename and archive used to live only in the expanded rail's row
                 kebab, so a viewport under EXPANDED_MIN_VIEWPORT could do
                 neither. Restore lives in the Archived panel, now reachable in
                 the drawer too. -->
            <ph-dropdown *ngIf="canManageActiveChat"
                         label="Chat options"
                         trigger-variant="ghost"
                         [items]="activeChatMenu"
                         (ph-select)="onActiveChatMenu($event.detail)"></ph-dropdown>
            <ph-tooltip text="Start new chat">
                <ph-compact-button variant="ghost" size="large" icon="plus" aria-label="Start new chat" (click)="onNewChat()"></ph-compact-button>
            </ph-tooltip>
            <ph-tooltip *ngIf="!isNarrowViewport" [text]="isExpanded ? 'Collapse to drawer' : 'Expand to full screen'">
                <ph-compact-button variant="ghost" size="large"
                                   [icon]="isExpanded ? 'arrows-in-simple' : 'arrows-out-simple'"
                                   [attr.aria-label]="isExpanded ? 'Collapse to drawer' : 'Expand to dialog'"
                                   (click)="onToggleDock()"></ph-compact-button>
            </ph-tooltip>
            <ph-tooltip text="Close">
                <ph-compact-button variant="ghost" size="large" icon="x" aria-label="Close drawer" (click)="onClose()"></ph-compact-button>
            </ph-tooltip>
        </header>

        <!-- The drawer's tab strip (Chat / Activity / Preferences / Skills)
             used to sit here. It is now the title-caret menu above, which also
             reaches recents and archived — things the tabs could not. -->

    <!-- SD-9208/SD-9209 — persistent scope banner (PMC portfolio or drill-down), above every view. -->
    <ph-banner *ngIf="isPmcScoped" variant="lighter" status="information" icon="buildings">
        <ng-container *ngIf="drillDownOrg; else portfolioScope">
            <span>Scoped to {{ drillDownOrg.name }}</span>
            <ph-link-button variant="primary" underline [disabled]="isSending" (click)="onExitDrillDown()">Back to portfolio</ph-link-button>
        </ng-container>
        <ng-template #portfolioScope>
            <span *ngIf="pmcName; else scopeFallback">Portfolio scope — all communities managed by {{ pmcName }}</span>
            <ng-template #scopeFallback><span>Portfolio scope — all communities in your portfolio</span></ng-template>
        </ng-template>
    </ph-banner>

    <ph-banner *ngIf="!isPmcScoped && foreignScopeLabel as label" variant="lighter" status="information" icon="buildings">
        <span>{{ label }}</span>
    </ph-banner>

    <!-- ============================================================ -->
    <!-- CONVERSATION — the thread scrolls, the composer stays pinned. -->
    <!-- Both wrappers need min-height: 0 or the thread refuses to     -->
    <!-- shrink and pushes the composer out of the dialog.             -->
    <!-- ============================================================ -->
    <div class="gable-conversation">
    <div class="gable-conversation-col">

    <!-- ============================================================ -->
    <!-- BODY (chat)                                                  -->
    <!-- ============================================================ -->
    <section class="gable-body" cdkScrollable *ngIf="activeTab === 'chat'" #conversationScroller (scroll)="onScroll()">

        <!-- LOADING SKELETON — shown while opening a past conversation so the
             switch feels instant and chat-shaped placeholders fill the gap. -->
        <ph-skeleton *ngIf="historyLoading" shape="chat" rows="2" label="Loading conversation"></ph-skeleton>

        <!-- EMPTY STATE -->
        <ng-container *ngIf="isEmptyState && !historyLoading">
            <ph-chat-welcome *ngIf="autopilotUi; else classicGreeting"
                             [greeting]="heroGreeting"
                             tagline="I keep an eye on your community around the clock and draft suggested actions across every module. Nothing is sent, charged, or changed until you approve it."
                             [viewAllLabel]="showHeroStrip ? 'View all ' + heroStripPending + ' item' + (heroStripPending === 1 ? '' : 's') + ' in the review queue' : ''"
                             [modules]="welcomeModules"
                             [columns]="isExpanded ? 3 : 1"
                             [suggestions]="suggestionItems"
                             [disabled]="isSending"
                             (ph-view-all)="onSelectTab('activity')"
                             (ph-module)="onSelectTab('activity', $event.detail)"
                             (ph-suggestion)="onSuggestionSelect($event.detail)">
                <ph-field *ngIf="isPmcScoped && !drillDownOrg">
                    <ph-label>Or focus on one community</ph-label>
                    <ph-input icon="magnifying-glass"
                              placeholder="Search communities"
                              aria-label="Search communities"
                              clearable
                              ngDefaultControl
                              [disabled]="isSending"
                              [ngModel]="communitySearch"
                              (ngModelChange)="onCommunitySearchChange($event)"
                              (focusin)="onCommunitySearchFocus()"></ph-input>
                    <ph-skeleton *ngIf="communityLoading && communityResults.length === 0" shape="list" rows="3" label="Loading communities"></ph-skeleton>
                    <ph-chat-suggestions #communityResultsList
                                         *ngIf="communityResults.length > 0"
                                         [items]="communityItems"
                                         [disabled]="isSending"
                                         (ph-select)="onCommunitySelect($event.detail)"></ph-chat-suggestions>
                    <ph-hint *ngIf="communitySearched && !communityLoading && communityResults.length === 0">No communities match your search.</ph-hint>
                </ph-field>
            </ph-chat-welcome>
            <ng-template #classicGreeting>
                <ph-chat-welcome greeting="Hi, I'm Gable! How can I help?"
                                 [suggestions]="suggestionItems"
                                 [disabled]="isSending"
                                 (ph-suggestion)="onSuggestionSelect($event.detail)"></ph-chat-welcome>
            </ng-template>
        </ng-container>

        <!-- CONVERSATION STATE -->
        <ng-container *ngIf="!isEmptyState && !historyLoading">
            <div
                class="gable-messages"
                [appGableMessageHighlight]="historySearch"
                (matchesChanged)="onMatchesChanged($event)">
                <ng-container *ngFor="let msg of messages; let i = index; trackBy: trackByIndex">
                    <!-- USER -->
                    <ph-chat-message role="user"
                                     *ngIf="msg.role === 'user'"
                                     [attr.data-user-msg-index]="i">
                        <ph-tag slot="attachments" *ngFor="let att of msg.attachments; trackBy: trackByAttachmentFileId" variant="gray" [icon]="attachmentKitIcon(att.fileName)">{{ att.fileName }}</ph-tag>{{ msg.content }}</ph-chat-message>

                    <!-- ASSISTANT -->
                    <div class="message assistant-message" *ngIf="msg.role === 'assistant'">
                        <!-- Sandbox deliverable still generating (SD-7240) —
                             skeleton variant of the deliverable card previews
                             exactly what's coming. -->
                        <!-- Static label on purpose: the live progress narration
                             (ai.sandbox.progress) streams into the thought trail,
                             NOT the card's document-name row — a streaming title
                             read as wonky. The card is a calm skeleton. -->
                        <app-gable-deliverable-card
                            *ngIf="msg.sandboxState === 'generating'"
                            [loading]="true"
                            loadingLabel="Working on it…"
                            loadingHint="Usually takes a minute or two">
                        </app-gable-deliverable-card>

                        <ph-alert
                            *ngIf="!msg.sandboxState && msg.alert"
                            [status]="alertStatus(msg.alert)"
                            variant="lighter"
                            size="small">{{ msg.content }}</ph-alert>

                        <ng-container *ngIf="!msg.sandboxState && !msg.alert">
                            <ng-container
                                *ngIf="msg.streamState === 'streaming' || (msg.thoughts && msg.thoughts.length > 0) || !!msg.thinkingContent; else loneGable">
                                <ph-chat-reasoning class="thought-trail-block"
                                                   [steps]="reasoningSteps(msg)"
                                                   [status]="msg.streamState === 'streaming' ? 'thinking' : 'done'"
                                                   [duration]="elapsedSeconds(msg) ?? undefined"
                                                   [folded]="msg.streamState === 'streaming' && (hasAnswerText(msg) || !msg.thoughts?.length)"
                                                   [label]="msg.streamState === 'streaming' && hasAnswerText(msg) ? currentStepLabel(msg) : ''"></ph-chat-reasoning>
                            </ng-container>
                            <ph-alert class="gable-long-run-notice"
                                      *ngIf="msg.longRunNotice && msg.streamState === 'streaming'"
                                      status="information"
                                      variant="lighter"
                                      size="small">{{ msg.longRunNotice }}</ph-alert>
                            <ng-template #loneGable></ng-template>

                            <!-- gable-suggestions fenced blocks are split out of the
                                 markdown and rendered as rich decision cards below
                                 the prose (raw JSON never shows; partial blocks are
                                 hidden until the closing fence streams in). -->
                            <ng-container *ngIf="msg.content | gableSuggestions as sg">
                                <div class="bubble-row" [class.is-streaming]="msg.streamState === 'streaming'">
                                    <div
                                        class="message-bubble"
                                        [class.is-streaming]="msg.streamState === 'streaming'"
                                        [class.is-failed]="msg.streamState === 'failed'"
                                        [attr.aria-live]="msg.streamState === 'streaming' ? 'polite' : null">
                                        <ph-chat-error *ngIf="msg.streamState === 'failed'; else answerText">{{ sg.text }}</ph-chat-error>
                                        <ng-template #answerText><ph-rich-text answer [markdown]="sg.text"></ph-rich-text></ng-template>
                                    </div>
                                </div>

                                <app-gable-suggestion-card
                                    *ngFor="let card of sg.cards; trackBy: trackBySuggestionCard"
                                    [payload]="card"
                                    (actionPrompt)="onSuggestionActionPrompt($event)">
                                </app-gable-suggestion-card>
                            </ng-container>

                            <!-- One "still working" heartbeat for the whole turn,
                                 pinned at the bottom. Runs while streaming until the
                                 answer starts rendering real results (a table / list) —
                                 so thought lines, preamble prose, and quiet
                                 tool-call gaps all read as one continuous live
                                 stream instead of a split thought/answer block. -->


                            <div class="citations" *ngIf="msg.citations && msg.citations.length > 0">
                                <span class="citations-label">Citations</span>
                                <span
                                    *ngFor="let cite of msg.citations; trackBy: trackByCitation"
                                    class="citation-pill">
                                    {{ cite.table }}#{{ cite.id }}
                                </span>
                            </div>

                            <ng-container *ngIf="msg.toolCards && msg.toolCards.length > 0">
                                <ng-container *ngFor="let card of msg.toolCards; trackBy: trackByToolCard">
                                    <app-gable-report-card
                                        *ngIf="asReportCardPayload(card) as reportPayload"
                                        [payload]="reportPayload">
                                    </app-gable-report-card>
                                    <app-gable-budget-redirect-card
                                        *ngIf="asBudgetRedirectPayload(card) as budgetPayload"
                                        [payload]="budgetPayload"
                                        [tool]="card.tool">
                                    </app-gable-budget-redirect-card>
                                    <app-gable-action-preview-card
                                        *ngIf="asActionPreviewPayload(card) as previewPayload"
                                        [attr.data-proposal-id]="previewPayload.proposal_id"
                                        [class.gable-card-attention]="attentionProposalId === previewPayload.proposal_id"
                                        [payload]="previewPayload"
                                        [pmcId]="cardPmcId"
                                        (confirm)="onActionPreviewConfirm($event)"
                                        (cancel)="onActionPreviewCancel($event)"
                                        (utilityAction)="onActionPreviewUtility($event)">
                                    </app-gable-action-preview-card>
                                    <app-gable-action-preview-card
                                        *ngIf="asCrudPreviewPayload(card) as crudPayload"
                                        [attr.data-proposal-id]="crudPayload.proposal_id"
                                        [class.gable-card-attention]="attentionProposalId === crudPayload.proposal_id"
                                        [payload]="crudPayload"
                                        [pmcId]="cardPmcId"
                                        (confirm)="onCrudPreviewConfirm($event)"
                                        (cancel)="onActionPreviewCancel($event)">
                                    </app-gable-action-preview-card>
                                    <app-gable-deliverable-card
                                        *ngIf="asDeliverableCardPayload(card) as deliverablePayload"
                                        [payload]="deliverablePayload">
                                    </app-gable-deliverable-card>
                                    <app-gable-ledger-intelligence-card
                                        *ngIf="asLedgerIntelligencePayload(card) as ledgerPayload"
                                        [payload]="ledgerPayload">
                                    </app-gable-ledger-intelligence-card>
                                </ng-container>
                            </ng-container>

            <!-- Feedback-submitted confirmation — replaces the thumbs row once
                 the user has picked a chip + Send. Locks further
                 feedback on this interaction. -->
            <ph-alert
                *ngIf="msg.streamState === 'complete' && msg.interactionId && wasFeedbackSent(msg) && !hasPendingSandboxCard"
                status="success"
                variant="lighter"
                size="small"
                class="feedback-submitted-alert">Thanks. Your feedback has been submitted to the team.</ph-alert>

                            <!-- Action row. The row itself is NOT gated on
                                 `wasFeedbackSent` — copy and regenerate stay
                                 useful after feedback is submitted; only the
                                 vote controls retire (they are
                                 replaced by the green confirmation above). -->
                            <div class="msg-footer"
                                 *ngIf="msg.streamState === 'complete' && msg.interactionId && !hasPendingSandboxCard"
                                 [gableAskSeen]="!!feedbackAskText[msg.interactionId]"
                                 (askSeen)="onFeedbackAskSeen(msg)">
                                <ph-chat-actions [copyText]="canCopyMessage(msg) ? copyableText(msg) : ''"
                                                 [vote]="getFeedback(msg.messageId) || ''"
                                                 [noVote]="wasFeedbackSent(msg)"
                                                 [ask]="feedbackAskText[msg.interactionId] || ''"
                                                 [animate]="!!freshAnswers[msg.messageId ?? 0]"
                                                 [thanks]="votedHere[msg.messageId ?? 0] && !isFeedbackPanelOpenFor(msg.messageId) ? 'Thanks for the feedback.' : ''"
                                                 (ph-vote)="onFeedback(msg, $event.detail)"></ph-chat-actions>
                            </div>

                            <!-- Inline reason-chips feedback panel — slides in below the thumbs row. -->
                            <app-gable-feedback-panel
                                *ngIf="msg.interactionId !== undefined && !wasFeedbackSent(msg) && !hasPendingSandboxCard"
                                [open]="isFeedbackPanelOpenFor(msg.messageId)"
                                (chipSelected)="onChipFeedback(msg, $event)"
                                (skipped)="closeFeedbackPanel()">
                            </app-gable-feedback-panel>

                            <!-- Shares the new-chat suggestion chip markup so the two stay identical. -->
                            <ph-chat-suggestions *ngIf="isLastAssistantMessage(i) && apiSuggestions.length > 0"
                                                 [items]="apiSuggestionItems"
                                                 [disabled]="isSending"
                                                 (ph-select)="onApiSuggestionClick($event.detail)"></ph-chat-suggestions>
                        </ng-container>
                    </div>
                </ng-container>

                <!-- Pre-stream thinking placeholder -->
                <div
                    class="message assistant-message"
                    *ngIf="isSending && !hasStreamingAssistant && (!pendingQuestions || submittingAnswers)">
                    <ph-chat-reasoning class="thought-trail-block" status="thinking" folded></ph-chat-reasoning>
                </div>

                <!-- Question card -->
                <app-question-card
                    *ngIf="pendingQuestions && !submittingAnswers"
                    [batch]="pendingQuestions"
                    [submitting]="submittingAnswers"
                    (submitAnswers)="onAnswers($event)"
                    (close)="onCloseQuestionCard()">
                </app-question-card>
            </div>

        </ng-container>
    </section>

    <!-- ============================================================ -->
    <!-- Activity — the review queue + feed (mock preview; SD-7969     -->
    <!-- wires it to pending proposals + the commit log).              -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel"
        cdkScrollable
        *ngIf="autopilotUi && activeTab === 'activity'">
        <app-gable-activity-panel [initialFilter]="activityFilter"></app-gable-activity-panel>
    </section>

    <!-- ============================================================ -->
    <!-- Skills — PayHOA's library for supers; an org's own skills for its admins (SD-9197, SD-10602). -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel"
        cdkScrollable
        *ngIf="activeTab === 'skills' && canManageWorkflows">
        <app-gable-skills-panel (switchToChat)="onSelectTab('chat')"></app-gable-skills-panel>
    </section>

    <section
        class="gable-body gable-body-panel"
        *ngIf="activeTab === 'ai-settings'">
        <ph-chat-gutter>
            <app-wb-ai-chatbot-status-card [enabled]="aiChatbotEnabled" (configure)="onAiSettingsConfigure()"></app-wb-ai-chatbot-status-card>
        </ph-chat-gutter>
    </section>

    <!-- ============================================================ -->
    <!-- Workflows: when + do + then, run on a schedule (SD-9963, org admins + supers). -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel"
        cdkScrollable
        *ngIf="activeTab === 'workflows' && canManageWorkflows">
        <app-gable-workflows-panel></app-gable-workflows-panel>
    </section>

    <!-- ============================================================ -->
    <!-- Preferences — standing "always…" / "never…" instructions      -->
    <!-- (SD-9309). Visible to every Gable user, not super-gated.       -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel gable-prefs-panel"
        cdkScrollable
        *ngIf="activeTab === 'prefs'">
        <ph-chat-gutter>
        <!-- Stands down while the composer is open; its CTA shares #prefsAddTrigger with the list's, and only one is ever mounted. -->
        <ph-stack *ngIf="prefsPanelEmpty && !prefsComposerOpen">
            <ph-empty-state icon="article" heading="No instructions yet" center>
                An instruction is a standing rule Gable follows in every conversation, without being
                asked. Add one here, or tell Gable to "always…" or "never…" in chat. They apply to
                your chats only.
                <ph-button #prefsAddTrigger slot="actions" icon="plus" (click)="onOpenPrefsComposer()">Add instruction</ph-button>
            </ph-empty-state>
            <ph-stack gap="tight" align="center">
                <ph-hint>Try one of these</ph-hint>
                <ph-chip *ngFor="let example of prefsExamples; trackBy: trackByExamplePreference"
                         (click)="onUseExamplePreference(example)">{{ example }}</ph-chip>
            </ph-stack>
        </ph-stack>

        <ph-page-header *ngIf="!prefsPanelEmpty && !preferencesLoading && !preferencesLoadFailed" dense size="small" [level]="2">
            Your instructions
            <ph-badge slot="title-actions" variant="lighter" color="gray" size="small">{{ preferences.length }}</ph-badge>
            <ph-button #prefsAddTrigger slot="actions" icon="plus" size="small" aria-label="Add instruction" (click)="onOpenPrefsComposer()">Add</ph-button>
        </ph-page-header>

        <!-- A handful of rules is read at a glance; search earns its row once the list is long. -->
        <ph-toolbar *ngIf="showPrefsSearch">
            <ph-input #prefsSearchInput
                      icon="magnifying-glass"
                      clearable
                      placeholder="Search instructions"
                      aria-label="Search instructions"
                      ngDefaultControl
                      [ngModel]="prefsSearch"
                      (ngModelChange)="onPrefsSearchChange($event)"
                      (keydown.escape)="$event.preventDefault(); onClearPrefsSearch()"></ph-input>
            <!-- Only once both sources are represented — see prefsSourceOptions. -->
            <ph-select *ngIf="prefsSourceOptions.length > 0"
                       aria-label="Filter by source"
                       [items]="prefsSourceItems"
                       [value]="prefsSourceFilter || filterAllValue"
                       (ph-change)="onPrefsSourceSelect($event.detail)"></ph-select>
        </ph-toolbar>

        <ph-skeleton *ngIf="preferencesLoading" shape="list" rows="3" label="Loading instructions"></ph-skeleton>

        <ph-hint *ngIf="prefsFiltering && !preferencesLoading && !preferencesLoadFailed && filteredPreferences.length > 0">{{ prefsCountLabel }}</ph-hint>

        <!-- `|| prefsComposerOpen` is load-bearing: the composer's only mount site is inside this stack. -->
        <ph-stack gap="snug" *ngIf="!preferencesLoading && !preferencesLoadFailed && (!prefsPanelEmpty || prefsComposerOpen)">
            <ng-container *ngIf="prefsComposerOpen">
                <ph-field>
                    <ph-textarea #prefsAddInput
                                 rows="1"
                                 max-rows="4"
                                 maxlength="500"
                                 placeholder="Add an instruction, e.g. always CC the board on violations"
                                 aria-label="Add an instruction"
                                 ngDefaultControl
                                 [ngModel]="newPreferenceText"
                                 (ngModelChange)="onNewPreferenceChange($event)"
                                 (keydown)="onPrefsKeydown($event)"></ph-textarea>
                    <!-- The visible count is not a live region; the hidden one speaks at milestones only. -->
                    <ph-toolbar justify="between" nowrap>
                        <ph-hint>{{ prefsCharsLeft !== null ? prefsCharsLeft + ' characters left' : '' }}</ph-hint>
                        <span class="visually-hidden" aria-live="polite">{{ prefsCharsLeftAnnouncement(newPreferenceText) }}</span> <!-- kitlint-allow-sr-only: milestone-only character count announcement -->
                        <ph-actions>
                            <ph-button variant="secondary" mode="filled" size="small" (click)="onClosePrefsComposer()">Cancel</ph-button>
                            <ph-button size="small"
                                       [disabled]="!newPreferenceText.trim() || savingPreference"
                                       (click)="onAddPreference()">Save</ph-button>
                        </ph-actions>
                    </ph-toolbar>
                </ph-field>
            </ng-container>

            <ng-container *ngFor="let pref of filteredPreferences; trackBy: trackByPreferenceId">
                <!-- Edit swaps the row's contents in place, so the rows around it don't move. -->
                <ng-container *ngIf="editingPreferenceId === pref.id; else readRow">
                    <ph-field>
                        <ph-textarea #prefsEditInput
                                     rows="1"
                                     max-rows="4"
                                     maxlength="500"
                                     aria-label="Edit instruction"
                                     ngDefaultControl
                                     [ngModel]="editingPreferenceText"
                                     (ngModelChange)="onEditPreferenceTextChange($event)"
                                     (keydown)="onPrefsEditKeydown($event, pref)"></ph-textarea>
                        <ph-toolbar justify="between" nowrap>
                            <ph-hint>{{ prefsEditCharsLeft !== null ? prefsEditCharsLeft + ' characters left' : '' }}</ph-hint>
                            <span class="visually-hidden" aria-live="polite">{{ prefsCharsLeftAnnouncement(editingPreferenceText) }}</span> <!-- kitlint-allow-sr-only: milestone-only character count announcement -->
                            <ph-actions>
                                <ph-button variant="secondary" mode="filled" size="small" (click)="onCancelEditPreference()">Cancel</ph-button>
                                <ph-button size="small"
                                           [disabled]="!editingPreferenceText.trim() || savingPreferenceEdit"
                                           (click)="onSavePreferenceEdit(pref)">Save</ph-button>
                            </ph-actions>
                        </ph-toolbar>
                    </ph-field>
                </ng-container>

                <ng-template #readRow>
                    <ph-list-row variant="card" size="small" wrap
                                 [heading]="pref.body"
                                 [meta]="preferenceMetaLabel(pref)"
                                 (ph-select)="onEditPreference(pref)">
                        <ph-dropdown slot="actions"
                                     label="Instruction options"
                                     trigger-variant="ghost"
                                     trigger-size="large"
                                     align="end"
                                     [items]="prefRowMenu"
                                     (ph-select)="onPrefRowMenu($event.detail, pref)"></ph-dropdown>
                    </ph-list-row>
                </ng-template>
            </ng-container>
        </ph-stack>

        <!-- Distinct from the empty state above: the list has rows, the filters just aren't matching any of them. -->
        <ph-empty-state *ngIf="!prefsPanelEmpty && !preferencesLoading && !preferencesLoadFailed && filteredPreferences.length === 0"
                        role="status"
                        icon="magnifying-glass"
                        [heading]="prefsEmptyMessage"
                        center></ph-empty-state>

        <ph-empty-state *ngIf="preferencesLoadFailed && !preferencesLoading" icon="warning" heading="Couldn't load your instructions" center>
            Something went wrong fetching your instructions.
            <ph-button slot="actions" variant="secondary" mode="filled" size="small" (click)="retryLoadPreferences()">Try again</ph-button>
        </ph-empty-state>
        </ph-chat-gutter>
    </section>

    <!-- ============================================================ -->
    <!-- Archived chats — full panel (search / filter / sort), not an  -->
    <!-- inline rail section. Renders in BOTH modes: the `*ngIf` below -->
    <!-- carries no mode test, and the drawer menu's Archived item     -->
    <!-- opens this panel. It was expanded-only until f390bf586, which -->
    <!-- collapsed the docked drawer's select-only archived submenu    -->
    <!-- into this one panel so a chat can still be restored below     -->
    <!-- 960px.                                                        -->
    <!-- SD-9563 F-b98e: this comment used to claim the opposite.      -->

    <!-- ============================================================ -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel gable-archived-panel"
        *ngIf="isOpen && activeTab === 'archived'">
        <ph-chat-gutter fill>
        <ph-empty-state *ngIf="archivedPanelEmpty" icon="archive" heading="No archived chats" center>
            Chats you've moved out of the way. Restore one to bring it back to recents.
        </ph-empty-state>

        <ph-toolbar>
            <ph-input #archivedSearchInput
                      icon="magnifying-glass"
                      clearable
                      placeholder="Search archived chats"
                      aria-label="Search archived chats"
                      ngDefaultControl
                      [ngModel]="archivedSearch"
                      (ngModelChange)="onArchivedSearchChange($event)"
                      (keydown.escape)="$event.preventDefault(); onClearArchivedSearch()"></ph-input>
            <ph-search-select *ngIf="archivedScopeOptions.length > 0"
                              aria-label="Filter by scope"
                              search-placeholder="Search communities"
                              [items]="archivedScopeItems"
                              [value]="archivedScopeFilter || filterAllValue"
                              (ph-change)="onArchivedScopeSelect($event.detail)"></ph-search-select>
            <ph-select aria-label="Sort archived chats"
                       [items]="archivedSortItems"
                       [value]="archivedSort"
                       (ph-change)="onArchivedSortChange($event.detail)"></ph-select>
        </ph-toolbar>

        <ph-skeleton *ngIf="archivedLoading && archivedRows.length === 0" shape="list" rows="3" label="Loading archived chats"></ph-skeleton>
        <ph-hint role="status" *ngIf="archivedRows.length === 0 && !archivedLoading">{{ archivedEmptyMessage }}</ph-hint>

        <cdk-virtual-scroll-viewport
            #archivedViewport
            class="gable-archived-viewport"
            [itemSize]="archivedRowHeightPx"
            minBufferPx="200"
            maxBufferPx="400"
            (scrolledIndexChange)="onArchivedScrolled()">
            <ng-container *cdkVirtualFor="let c of archivedRows; trackBy: trackByConversationId">
                <ph-input
                    *ngIf="editingConversationId === c.id; else archivedRow"
                    autofocus
                    maxlength="255"
                    aria-label="Rename chat"
                    [value]="editingTitle"
                    (input)="onRailRenameInput($event)"
                    (keydown.enter)="$event.preventDefault(); commitInlineRename(c)"
                    (keydown.escape)="$event.preventDefault(); cancelInlineRename()"
                    (focusout)="commitInlineRename(c)"
                    (click)="$event.stopPropagation()"></ph-input>
                <ng-template #archivedRow>
                    <ph-list-row [heading]="conversationLabel(c)"
                                 [meta]="archivedScopeLabel(c) ? archivedDateLabel(c) + ', ' + archivedScopeLabel(c) : archivedDateLabel(c)"
                                 (ph-select)="onSelectConversation(c)">
                        <ph-dropdown slot="actions"
                                     label="Chat options"
                                     align="end"
                                     trigger-variant="ghost"
                                     trigger-size="large"
                                     [items]="archivedRowMenu"
                                     (click)="$event.stopPropagation()"
                                     (ph-select)="onArchivedRowMenu($event.detail, c)"></ph-dropdown>
                    </ph-list-row>
                </ng-template>
            </ng-container>
        </cdk-virtual-scroll-viewport>
        </ph-chat-gutter>
    </section>

    <!-- SD-9246 — calm notice for the frozen 409 turn_in_flight contract; not the red error state. -->
    <ph-alert *ngIf="turnInFlightNotice && activeTab === 'chat'" status="information" variant="lighter">
        <span>Gable is still working on your previous request&hellip;</span>
        <ph-link-button *ngIf="draftMessage.trim()" variant="primary" (click)="askInNewChat()">Ask in a new chat</ph-link-button>
    </ph-alert>

    <!-- SD-10526 — box at capacity; auto-resends the same turn, not the red error state. -->
    <ph-alert *ngIf="busySecondsRemaining !== null && activeTab === 'chat'" status="information" variant="lighter">
        <span>{{ busyNoticeText }}</span>
    </ph-alert>

    <!-- SD-10526 — Echo/Ably transport dropped mid-turn; the box keeps working, so this is not the red error state either. -->
    <ph-alert *ngIf="connectionLostNotice && activeTab === 'chat'" status="information" variant="lighter">
        <span>Connection to the assistant is spotty right now. Still checking on your answer&hellip;</span>
    </ph-alert>

    <!-- ============================================================ -->
    <!-- FOOTER / COMPOSER                                            -->
    <!-- ============================================================ -->
    <footer class="gable-footer" *ngIf="!pendingQuestions && activeTab === 'chat'">
        <ph-composer #messageInput
                     [value]="composerValue" [placeholder]="composerPlaceholder"
                     [maxLength]="composerMaxLength" [maxRows]="composerMaxRows"
                     [busy]="isSending && !backgroundMode" [disabled]="isSending && !backgroundMode"
                     [canAttach]="true" attach-label="Attach a file" attach-menu
                     [attachments]="composerAttachments" [documents]="documentLibrary" [documentCategories]="documentCategories"
                     [canDictate]="dictation.supported" [listening]="dictation.listening"
                     [notice]="dictation.error || ''" [meta]="composerMeta"
                     (ph-submit)="onSend()" (ph-stop)="onStopStream()"
                     (ph-attach)="onAttachClick()" (ph-attach-image)="onAttachImageClick()"
                     (ph-attach-documents)="onComposerAttachDocuments($event.detail)"
                     (ph-remove-attachment)="onComposerRemoveAttachment($event.detail)"
                     (ph-clear-attachments)="onComposerClearAttachments()"
                     (ph-mic)="onToggleDictation()" (ph-mic-confirm)="onConfirmDictation()"
                     (ph-mic-cancel)="onCancelDictation()"
                     (ph-notice-dismiss)="onDismissDictationError()"></ph-composer>
        <input #attachmentFileInput type="file" hidden multiple
               accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
               (change)="onAttachmentFileSelected($event)">
        <input #attachmentImageInput type="file" hidden multiple
               accept=".png,.jpg,.jpeg,.gif,.webp"
               (change)="onAttachmentFileSelected($event, 'image')">
        <p class="gable-disclaimer">Gable can make mistakes. Double-check important info.</p>
    </footer>

    </div><!-- /.gable-conversation-col -->
    </div><!-- /.gable-conversation -->

    </div><!-- /.gable-main -->
    </div><!-- /.gable-content-row -->
</div>
</ng-template>

<app-confirm-dialog [open]="!!pendingDeletePreference"
                    heading="Delete this instruction?"
                    message="Gable will stop applying this instruction in future chats."
                    confirmLabel="Delete"
                    [destructive]="true"
                    (closed)="onDeletePreferenceClosed($event)"></app-confirm-dialog>
